/* global gdAlertify */
import React, { useState, useMemo } from "react";
import BuscarPaciente from "./components/BuscarPaciente";
import { formatDate, formatMoney, formatSectorLabel } from "../utils/utils";
import GdTable from "./components/GdTable";
import { getResumenAnualTurnos } from "../services/turnosService";

const KpiCard = ({ titulo, valor, icono, variante }) => (
    <div className={`card border-${variante} h-100`}>
        <div className="card-body py-3">
            <div className="d-flex align-items-center justify-content-between">
                <div>
                    <div className="text-muted small">{titulo}</div>
                    <div className={`fs-4 fw-bold text-${variante}`}>{valor}</div>
                </div>
                <i className={`bi ${icono} fs-2 text-${variante} opacity-50`}></i>
            </div>
        </div>
    </div>
);

const getAuxiliarEfectivo = (row) => {
    if (row.reemplazoNombre && row.auxiliarID != null && row.es_reemplazo == 0) {
        return row.reemplazoNombre;
    }
    return row.auxiliarNombre;
};

const getValorTurnoActual = (row = {}) =>
    Number(row.valorTurnoRt || row.reemplazoValorTurno || row.valorTurno || 0);

const getValorMovilizacionActual = (row = {}) => {
    if (row.reemplazoValorTurno > 0 && row.es_reemplazo == 0) {
        return Number(row.reemplazoValorMovilizacion ?? row.valorLocomocionRt ?? row.valorMovilizacion ?? 0);
    }
    return Number(row.valorLocomocionRt ?? row.reemplazoValorMovilizacion ?? row.valorMovilizacion ?? 0);
};

const getValorColacionActual = (row = {}) => {
    if (row.reemplazoValorTurno > 0 && row.es_reemplazo == 0) {
        return Number(row.reemplazoValorColacion ?? row.valorColacionRt ?? row.valorColacion ?? 0);
    }
    return Number(row.valorColacionRt ?? row.reemplazoValorColacion ?? row.valorColacion ?? 0);
};

const ResumenTurnosCoordinacion = () => {
    const [anio, setAnio] = useState(new Date().getFullYear());
    const [searchPatient, setSearchPatient] = useState(null);
    const [turnos, setTurnos] = useState([]);
    const [kpis, setKpis] = useState(null);
    const [cargado, setCargado] = useState(false);

    const cargarDatos = async (anioVal, paciente) => {
        try {
            gdAlertify.showLoader();
            const data = await getResumenAnualTurnos(anioVal, paciente?.id ?? null);
            if (!data.error) {
                setTurnos(data.turnos || []);
                setKpis(data.kpis || null);
                setCargado(true);
            } else {
                gdAlertify.notiError(data.message || "Error al obtener datos");
            }
        } catch (err) {
            console.error(err);
            gdAlertify.notiError("Error al conectar con el servidor.");
        } finally {
            gdAlertify.hideLoader();
        }
    };

    const handleBuscar = () => cargarDatos(anio, searchPatient);

    const getDayOfWeek = (dateString) => {
        const days = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
        const date = new Date(dateString);
        return days[date.getDay()];
    };

    const colorPalette = [
        "#C62828", "#1565C0", "#2E7D32", "#F9A825", "#6A1B9A",
        "#00897B", "#FF8F00", "#4527A0", "#0277BD", "#5D4037",
        "#AD1457", "#283593", "#558B2F", "#D84315", "#303F9F",
        "#FF6F00", "#455A64", "#9E9D24", "#4E342E", "#00838F"
    ];
    const nombreColorMap = {};
    let nextColorIndex = 0;
    const getColorForNombre = (nombre) => {
        if (nextColorIndex === 20) nextColorIndex = 0;
        if (!nombreColorMap[nombre]) {
            nombreColorMap[nombre] = colorPalette[nextColorIndex % colorPalette.length];
            nextColorIndex++;
        }
        return nombreColorMap[nombre];
    };

    const columns = useMemo(() => [
        {
            title: "Sector",
            name: "pacienteSector",
            render: (row) => <div className="text-center">{formatSectorLabel(row.pacienteSector)}</div>,
            width: 55,
        },
        {
            title: "Nombre Paciente",
            name: "pacienteNombre",
            render: (row) => (
                <span style={{ color: getColorForNombre(row.pacienteNombre) }}>
                    <b>{row.pacienteNombre}</b>
                </span>
            ),
            width: 230,
        },
        {
            title: "RUT Paciente",
            name: "pacienteRut",
            render: (row) => <b>{row.pacienteRut}</b>,
            width: 120,
        },
        {
            title: "Nombre Auxiliar",
            name: "auxiliarNombre",
            render: (row) => {
                const nombre = getAuxiliarEfectivo(row);
                const esReemplazo = row.reemplazoNombre && row.auxiliarID != null && row.es_reemplazo == 0;
                return (
                    <div className="d-flex align-items-center gap-1 flex-wrap">
                        <span>{nombre}</span>
                        {esReemplazo && (
                            <span className="badge bg-primary" style={{ fontSize: "0.6rem" }}>
                                Reemplazo
                            </span>
                        )}
                        {Number(row.totalAdicionales) > 0 && (
                            <span className="badge bg-warning text-dark" style={{ fontSize: "0.6rem" }}>
                                +{row.totalAdicionales} adic.
                            </span>
                        )}
                    </div>
                );
            },
            getFilterValue: (row) => getAuxiliarEfectivo(row),
            width: 250,
        },
        {
            title: "RUT Auxiliar",
            name: "auxiliarRut",
            render: (row) => <b>{row.auxiliarRut}</b>,
            width: 120,
        },
        {
            title: "Día",
            name: "diaSemana",
            render: (row) => (
                <span className={row.turno == "1" ? "text-blue" : "text-red"}>
                    {getDayOfWeek(row.fecha)}
                </span>
            ),
            getFilterValue: (row) => getDayOfWeek(row.fecha),
            width: 100,
        },
        {
            title: "Fecha",
            name: "fecha",
            type: "date",
            render: (row) => (
                <span className={row.turno == "1" ? "text-blue" : "text-red"}>
                    {formatDate(row.fecha)}
                </span>
            ),
            getFilterValue: (row) => formatDate(row.fecha),
            width: 120,
        },
        {
            title: "Turno",
            name: "tipo",
            render: (row) => (
                <span className={row.turno == "1" ? "text-blue" : "text-red"}>
                    {row.turno == "1" ? "Día" : "Noche"}
                    {row.es_apoyo === 1 && (
                        <span
                            className="ms-1 badge"
                            style={{ fontSize: "0.6rem", backgroundColor: "#6f42c1", verticalAlign: "middle" }}
                        >
                            Apoyo
                        </span>
                    )}
                </span>
            ),
            getFilterValue: (row) => `${row.turno == "1" ? "Día" : "Noche"}${row.es_apoyo === 1 ? " Apoyo" : ""}`,
            width: 80,
        },
        {
            title: "Valor Turno",
            name: "valorTurno",
            type: "number",
            render: (row) => {
                const valor = getValorTurnoActual(row);
                return <div className="text-end">{valor > 0 ? `$${formatMoney(valor)}` : ""}</div>;
            },
            getFilterValue: (row) => String(getValorTurnoActual(row)),
            width: 90,
        },
        {
            title: "Valor Movili..",
            name: "valorMovilizacion",
            type: "number",
            render: (row) => {
                const valor = getValorMovilizacionActual(row);
                return <div className="text-end">{valor > 0 ? `$${formatMoney(valor)}` : ""}</div>;
            },
            getFilterValue: (row) => String(getValorMovilizacionActual(row)),
            width: 90,
        },
        {
            title: "Valor Colación",
            name: "valorColacion",
            type: "number",
            render: (row) => {
                const valor = getValorColacionActual(row);
                return <div className="text-end">{valor > 0 ? `$${formatMoney(valor)}` : ""}</div>;
            },
            getFilterValue: (row) => String(getValorColacionActual(row)),
            width: 90,
        },
        {
            title: "Notas",
            name: "notes",
            width: 300,
        },
    ], [turnos]);

    const getRowClass = (row) => {
        if (row?.es_eliminado) return "bg-row-eliminado";

        if (row?.color === 2) return "bg-row-reemplazo";
        if (row?.color === 3) return "bg-row-marca";
        if (row?.color === 1) return "";

        if (row?.solicitud_reemplazo && row.solicitud_estado == 0)
            return "bg-row-solicitud-reemplazo";

        if (row?.solicitud_reemplazo) return "bg-row-reemplazo";

        if (row?.es_reemplazo || row?.es_apoyo) return "bg-row-reemplazo";

        if (row?.marca) return "bg-row-marca";

        if (row?.vive_fuera_santiago == 1) return "bg-row-verde";

        if (Number(row?.totalAdicionales) > 1) return "bg-row-rojo-claro";

        return "";
    };

    return (
        <div className="container-fluid mt-4">
            <div className="card">
                <div className="card-header">
                    <h4 className="mb-0">Resumen de Turnos</h4>
                </div>
                <div className="card-body">

                    {/* Filtros */}
                    <div className="row mb-3 align-items-end">
                        <div className="col-md-4 mb-3">
                            <BuscarPaciente
                                onSelect={(p) => setSearchPatient(p)}
                                onClear={() => setSearchPatient(null)}
                            />
                        </div>
                        <div className="col-md-2 mb-3">
                            <label className="form-label">Año</label>
                            <select
                                className="form-select"
                                value={anio}
                                onChange={(e) => setAnio(Number(e.target.value))}
                            >
                                {[2023, 2024, 2025, 2026, 2027, 2028].map((y) => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2 mb-3">
                            <button className="btn btn-primary w-100" onClick={handleBuscar}>
                                Buscar Turnos
                            </button>
                        </div>
                    </div>

                    {/* KPIs */}
                    {kpis && (
                        <div className="row g-3 mb-4">
                            <div className="col-6 col-md-3">
                                <KpiCard titulo="Total Pacientes"  valor={kpis.totalPacientes}  icono="bi-person-heart"  variante="primary" />
                            </div>
                            <div className="col-6 col-md-3">
                                <KpiCard titulo="Total Auxiliares" valor={kpis.totalAuxiliares} icono="bi-people"        variante="success" />
                            </div>
                            <div className="col-6 col-md-3">
                                <KpiCard titulo="Turnos Día"       valor={kpis.totalDia}        icono="bi-sun"           variante="warning" />
                            </div>
                            <div className="col-6 col-md-3">
                                <KpiCard titulo="Turnos Noche"     valor={kpis.totalNoche}      icono="bi-moon-stars"    variante="info"    />
                            </div>
                        </div>
                    )}

                    {/* Tabla */}
                    {cargado && (
                        <>
                            <GdTable
                                columns={columns}
                                data={turnos}
                                itemsPerPageOptions={[50, 100, 300, 1000, 2000, 6000]}
                                defaultItemsPerPage={20000}
                                tableClassName="table table-bordered"
                                showCsvExport={true}
                                height={700}
                                separadorPorCampo="pacienteNombre"
                                virtualize
                                exportFilename={`resumen-turnos-${anio}.xlsx`}
                                getRowClass={getRowClass}
                            />
                            {turnos.length === 0 && (
                                <div className="alert alert-info mt-2">
                                    No se encontraron turnos para el año {anio}.
                                </div>
                            )}
                        </>
                    )}

                    {!cargado && (
                        <div className="text-center text-muted py-5">
                            <i className="bi bi-calendar3 fs-1 opacity-25"></i>
                            <p className="mt-2">Seleccione el año y presione "Buscar Turnos" para ver el resumen.</p>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default ResumenTurnosCoordinacion;
